/**
 * @license
 * Copyright 2022 Superflow.dev
 * SPDX-License-Identifier: MIT
 */

import { LitElement, html, css, PropertyValueMap } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import Util from './util';
/*

Modes: multiselect, select
Flows: view,edit

*/

/**
 * SfTableDisplay element.
 * @fires renderComplete - When the list is populated
 * @fires valueChanged - When the value is changed
 * @property apiId - backend api id
 * @property label - input label
 * @property name - name of the input
 */
@customElement('sf-table-display')
export class SfTableDisplay extends LitElement {
  static override styles = css`
    :root {
      --color: #fcc419;
      --shadow: #f08c00;
      --glare: hsl(0 0% 100% / 0.75);
      --font-size: clamp(.75rem, 2vw, 1rem);
      --transition: 0.2s;
    }

    * {
      box-sizing: border-box;
    }

    .d-block {
      display: block;
    }

    .d-flex {
      display: flex;
    }

    .flex-col {
      flex-direction: column;
    }

    .flex-wrap {
      flex-wrap: wrap;
    }

    .justify-center {
      justify-content: center;
    }

    .justify-between {
      justify-content: space-between;
    }

    .justify-end {
      justify-content: flex-end;
    }

    .w-100-m-0 {
      width: 100%;
    }
    
    .mb-20 {
      margin-bottom: 20px;
    }
    
    .mt-20 {
      margin-top: 20px;
    }
    .mr-10 {
      margin-right: 10px;
    }
    .mr-5 {
      margin-right: 5px;
    }
    .m-0 {
      margin: 0px;
    }

    .m-20 {
      margin: 20px;
    }
    .md-20-ml-10 {
      margin-top: 20px;
      margin-bottom: 20px;
      margin-left: 10px;
      margin-right: 10px;
    }
    .mrl-5 {
      margin-left: 5px;
      margin-right: 5px;
    }
    .mrl-10 {
      margin-left: 10px;
      margin-right: 10px;
    }
    .mt-10{
      margin-top: 10px
    }
    .ml-10{
      margin-left: 10px
    }
    .mr-20{
      margin-right: 20px
    }
    .flex-grow {
      flex-grow: 1;
    }
    .align-start {
      align-items: flex-start;
    }

    .align-end {
      align-items: flex-end;
    }

    .align-center {
      align-items: center;
    }
    .align-stretch {
      align-items: stretch;
    }
    .align-self-start {
      align-self: flex-start;
    }
    body {
      display: grid;
      place-items: center;
      min-height: 100vh;
      font-family: 'Google Sans', sans-serif, system-ui;
    }
    .hide {
      display: none;
    }
    .invisible {
      visibility: hidden;
    }
    .scroll-x {
      overflow-x: scroll
    }
  `;

  @property({ type: Boolean })
  search = false

  tableData: any[] = []
  // tableData: any[] = [{ "id": "01/27/2026;940bb95a-eb7e-49de-810c-ed6591c8a453;7b265682-7daf-475d-b617-313a49a55f5d;634a8c2a-30f9-44f8-8cda-99d81297747d_0", "reportingDate": "01/27/2026", "country": "India", "state": "", "statute": "", "category": "ESG", "subCategory": "Labour and Employment", "authority": "Ministry of Environment, Forest and Climate Change ", "notificationName": "Control of Water Pollution (Grant, Refusal or Cancellation of Consent) Amendment Guidelines, 2026 and Control of Air Pollution (Grant, Refusal or Cancellation of Consent) Amendment Guidelines, 2026", "notificationReference": "1. G.S.R. 62(E)\n2. G.S.R. 63(E)", "publicationDate": "2026-01-27", "effectiveDate": "2026-01-27", "remarks": "Ministry of Environment, Forest and Climate Change has been issued making changes in Control of Water Pollution (Grant, Refusal or Cancellation of Consent) Amendment Guidelines, 2026 and Control of Air Pollution (Grant, Refusal or Cancellation of Consent) Amendment Guidelines, 2026, by inserting and substituting some Guidlines under the same", "sourceImpactName": "Came into force", "sourceTypeName": "Amendment in existing compliance obligations", "toolUpdationStatus": "" }, { "id": "01/27/2026;940bb95a-eb7e-49de-810c-ed6591c8a453;7b265682-7daf-475d-b617-313a49a55f5d;634a8c2a-30f9-44f8-8cda-99d81297747d_1", "reportingDate": "01/27/2026", "country": "India", "state": "", "statute": "", "category": "ESG", "subCategory": "Labour and Employment", "authority": "Ministry of Environment, Forest and Climate Change", "notificationName": "Solid Waste Management Rules, 2026", "notificationReference": "S.O. 388(E)", "publicationDate": "2026-01-28", "effectiveDate": "2026-04-01", "remarks": "Ministry of Environment, Forest and Climate Change has published new Solid Waste Management Rules, 2026 repealing the previous Solid Waste Management Rules, 2016", "sourceImpactName": "Enacted (not yet effective)", "sourceTypeName": "New rules under existing Act (in suppression of old rules)", "toolUpdationStatus": "As per effective date" }]

  tablecols: string[] = []

  filters: { [key: string]: { [key: string]: boolean } } = {};

  initListeners = () => {
    let dropDownButton = this.shadowRoot?.getElementById("dropdown-button") as HTMLButtonElement;
    dropDownButton?.addEventListener('click', (e) => {
      let multiselectList = this.shadowRoot?.getElementById("select-list") as HTMLDivElement;
      let selectedList = this.shadowRoot?.getElementById("selected-list") as HTMLDivElement;
      let button = e.target as HTMLButtonElement;
      if (button.innerHTML === "keyboard_arrow_down") {
        multiselectList.classList.remove("hide");
        selectedList.classList.add("hide");
        button.innerHTML = "keyboard_arrow_up"
      } else {
        multiselectList.classList.add("hide");
        selectedList.classList.remove("hide");
        button.innerHTML = "keyboard_arrow_down"
      }

    })

    let searchInput = this.shadowRoot?.getElementById("search-input") as HTMLInputElement;
    searchInput?.addEventListener('input', (e) => {
      let searchInput = e.target as HTMLInputElement;
      let filterText = searchInput.value.toLowerCase();
      if (filterText !== "") {
        let multiselectList = this.shadowRoot?.getElementById("select-list") as HTMLDivElement;
        multiselectList.classList.remove("hide");
        let dropdownButton = this.shadowRoot?.getElementById("dropdown-button") as HTMLButtonElement;
        dropdownButton.innerHTML = "keyboard_arrow_up"
      }
    })

  }

  populateTable = (searchVal: string = "") => {
    this.tablecols = []
    let linesCount = 0;
    let searchFilterString = ""
    let flagUpdateFilters = Object.keys(this.filters).length == 0;
    let html = `${this.search ? `
          <div part="table-search-container" class="d-flex flex-col align-stretch w-100-m-0">
              <label part="table-search-label">Search</label>
              <div class="d-flex">
                <input id="table-search" part="table-search" type="text" autofocus/>
                <button id="button-search" part="button" class="button-search d-flex justify-center align-center ml-10"><span class="material-symbols-outlined">search</span></button>
              </div>
              <div id="filter-string" part="filter-string" class="d-flex align-center">Test String</div>
          </div>
          ` : `
          <div part="table-search-container" class="d-flex flex-col align-stretch w-100-m-0">
              <div id="filter-string" part="filter-string" class="d-flex align-center">Test String</div>
          </div>
          `}`
    html += '<table class="w-100-m-0 d-block scroll-x">'
    let tableBodyHtml = ''
    for (var j = 0; j < this.tableData.length; j++) {

      // const complianceId = Object.keys(objCountry[statute])[j];
      // // const compliance = objCountry[statute][complianceId];
      // // console.log('hidecompliances', compliance.hide, complianceId);

      const data = Object.values(this.tableData[j]);
      const cols = Object.keys(this.tableData[j]);

      for (let col of cols) {
        if (!this.tablecols.includes(col) && col != 'hide') {
          this.tablecols.push(col)
          if (flagUpdateFilters && this.filters[col] == null) {
            this.filters[col] = {}
          }
        }

      }
      if (this.tableData[j].hide != null && this.tableData[j].hide == true) {
        continue;
      }
      linesCount++

      tableBodyHtml += '<tr>';

      // tableBodyHtml += ('<td class="td-body" part="td-body-table"><span part="td-head" style="padding-left: 0px !important">ID</span><br /><sf-i-elastic-text exportparts="highlight,highlight-count" text="' + complianceId + '" minLength="10" lineSize="4"></sf-i-elastic-text></td>');
      let tempCount = 0;
      for (var k = 0; k < cols.length; k++) {
        if (this.tablecols.indexOf(cols[k]) == tempCount) {
          // tableBodyHtml += ('<td class="td-body" part="td-body-table"><span part="td-head" style="padding-left: 0px !important">' + cols[k] + '</span><br /><sf-i-elastic-text exportparts="highlight,highlight-count" text="' + data[k] + '" minLength="80" lineSize="4"></sf-i-elastic-text></td>');
          tableBodyHtml += ('<td class="td-body" part="td-body-table"><sf-i-elastic-text exportparts="highlight,highlight-count" text="' + data[k] + '" highlight="' + searchVal + '" minLength="80" lineSize="4"></sf-i-elastic-text></td>');
          if (flagUpdateFilters) {
            console.log('rendering', data[k] as string);
            this.filters[cols[k]][(data[k] as string)] = true;
          }
          tempCount++;
        }
      }
      tableBodyHtml += '</tr>';
    }
    html += '<thead><tr>';
    searchFilterString = ""
    for (let colName of this.tablecols) {
      let allSelected = true;
      for (let subfilterObj of Object.keys(this.filters[colName] ?? {})) {
        if (this.filters[colName][subfilterObj] == false) {
          allSelected = false;
          for (let subfilterObjVal of Object.keys(this.filters[colName] ?? {})) {
            if (this.filters[colName][subfilterObjVal] == true) {
              if (searchFilterString == "") {
                searchFilterString += subfilterObjVal
              } else {
                searchFilterString += ', ' + subfilterObjVal
              }
            }
          }
          break;
        }
      }

      html += '<th class="td-body" part="td-body-table"><div class="d-flex align-center"><span part="' + (allSelected ? 'td-head' : 'td-head-selected') + '" style="' + (allSelected ? 'padding-left: 0px !important' : '') + '">' + colName + '</span><div class="filter-button-container ml-10"><button part="' + (allSelected ? 'col-icon' : 'col-icon-selected') + '" id="filter-button-' + this.tablecols.indexOf(colName) + '" class="filter-button material-symbols-outlined">filter_list</button>'
      html += '<div id="filter-list-container-' + this.tablecols.indexOf(colName) + '" class="filter-list-container hide flex-col justify-between align-start p-10" part="filter-list-container" anchor="filter-button">'
      html += `<div id="filter-label-container-${this.tablecols.indexOf(colName)}" class="filter-label-container d-flex justify-between align-center" part="filter-label-container"><div class="d-flex align-center"><input class="input-select-filter" type="checkbox" ${allSelected ? 'checked' : ''} id="input-select-filter-${this.tablecols.indexOf(colName)}-all" name="features" part="input-select-feature" value="all-${colName}"></input><label class="filter-label" part="filter-label" for="input-select-filter-${this.tablecols.indexOf(colName)}-all">All</label></div><button id="button-submit-filters-${this.tablecols.indexOf(colName)}" part="col-option-icon" class="button-submit-filters material-icons invisible">check</button></div>`
      for (let [index, filterKey] of Object.keys(this.filters[colName]).entries()) {
        // console.log('rendering filter', filterKey);
        html += `<div class="filter-input-container d-flex justify-start align-start"><input class="input-select-filter" type="checkbox" ${this.filters[colName][filterKey] == true ? 'checked' : ''} id="input-select-filter-${this.tablecols.indexOf(colName)}-${index}" name="features" part="input-select-feature" value="${filterKey}"></input><label for="input-select-filter-${this.tablecols.indexOf(colName)}-${index}" id="input-select-filter-label-${this.tablecols.indexOf(colName)}-${index}" class="input-select-filter-label"  part="input-select-feature-label">${filterKey}</label></div>`
      }
      html += '</div></div></th>'
    }
    html += '</tr></thead>';
    html += tableBodyHtml;
    html += '</table>';
    const divTableList = (this.shadowRoot?.querySelector('#table-list') as HTMLDivElement);
    if (divTableList == null) {
      return;
    }
    divTableList.innerHTML = html;

    const divFilterString = divTableList.querySelector('#filter-string') as HTMLDivElement
    let filterString = ""
    if (searchVal != "" || searchFilterString != "") {
      filterString = linesCount + " result(s) when "
      if (searchVal != "") {
        filterString += "searched by \"" + searchVal + "\""
        if (searchFilterString != "") {
          filterString += ", filtered by \"" + searchFilterString + "\""
        }
      } else if (searchFilterString != "") {
        filterString += "filtered by \"" + searchFilterString + "\""
      }
    }
    if (filterString != "") {
      filterString += '<span class="material-symbols-outlined ml-5" id="filter-delete" part="filter-delete">delete</span>'
    }
    divFilterString.innerHTML = filterString
    const filterDeleteButton = divFilterString.querySelector('#filter-delete') as HTMLSpanElement;
    filterDeleteButton?.addEventListener('click', () => {
      for (let col of Object.keys(this.filters)) {
        for (let filterVal of Object.keys(this.filters[col])) {
          this.filters[col][filterVal] = true;
        }
      }
      this.processRegisterFilters("");
    })

    const arrInputStatuteSearch = divTableList.querySelectorAll('#table-search') as NodeListOf<HTMLInputElement>;
    for (let i = 0; i < arrInputStatuteSearch.length; i++) {
      const input = Util.clearListeners(arrInputStatuteSearch[i]);
      input.addEventListener('change', (e: any) => {
        const searchedValue = e.currentTarget.value;
        // const objCountry = events[Object.keys(events)[countryIndex]]
        // const statute = Object.keys(objCountry)[searchIndex];
        // for (var j = 0; j < Object.keys(objCountry[statute]).length; j++) {
        //   const complianceId = Object.keys(objCountry[statute])[j];
        //   const compliance = objCountry[statute][complianceId];
        //   if (JSON.stringify(compliance).toLowerCase().includes(searchedValue.toLowerCase())) {
        //     events[Object.keys(events)[countryIndex]][statute][complianceId].hide = false;
        //   } else {
        //     events[Object.keys(events)[countryIndex]][statute][complianceId].hide = true;
        //   }
        // }

        this.processRegisterFilters(searchedValue);
        // this.renderRegisterEvents(events, meta, countryIndex, searchIndex, searchedValue);

      })
    }
    const arrButtonStatuteSearch = divTableList.querySelectorAll('.button-search') as NodeListOf<HTMLButtonElement>;
    for (let i = 0; i < arrButtonStatuteSearch.length; i++) {
      const button = Util.clearListeners(arrButtonStatuteSearch[i]);
      button.addEventListener('click', (e: any) => {
        const inputId = e.currentTarget.id.replace('button-', 'stream-')
        const searchedValue = divTableList.querySelector(inputId).value;
        this.processRegisterFilters(searchedValue);

      })
    }

    const arrFilterButtons = divTableList.querySelectorAll('.filter-button') as NodeListOf<HTMLButtonElement>;
    for (let i = 0; i < arrFilterButtons.length; i++) {
      const button = Util.clearListeners(arrFilterButtons[i]);
      button.addEventListener('click', (e: any) => {
        const index = e.currentTarget.id.split('-')[2];
        let filterListContainer = divTableList.querySelector('#filter-list-container-' + index) as HTMLDivElement;
        if (filterListContainer != null) {
          if (filterListContainer.classList.contains('hide')) {
            filterListContainer.classList.remove('hide');
          } else {
            filterListContainer.classList.add('hide');
          }
        }
      })
    }

    const arrFilterInputs = divTableList.querySelectorAll('.input-select-filter') as NodeListOf<HTMLInputElement>;
    for (let i = 0; i < arrFilterInputs.length; i++) {
      const input = Util.clearListeners(arrFilterInputs[i]);
      input.addEventListener('change', (e: any) => {
        let checked = e.currentTarget.checked;
        let selectedColIndex = e.currentTarget.id.split('-')[3];
        let selectedFilterIndex = e.currentTarget.id.split('-')[4];
        let colName = this.tablecols[selectedColIndex]
        // for (let [colIndex, colName] of Object.keys(this.registerFilters).entries()) {
        if (colName != null) {
          for (let [filterIndex, filterKey] of Object.keys(this.filters[colName]).entries()) {
            if (filterIndex == parseInt(selectedFilterIndex) || selectedFilterIndex == 'all') {
              this.filters[colName][filterKey] = checked;
              // console.log('registerFilters entry', this.filters[colName][filterKey], colName, filterKey);
            }
            if (selectedFilterIndex == 'all') {
              (divTableList.querySelector('#input-select-filter-' + selectedColIndex + '-' + filterIndex) as HTMLInputElement).checked = checked;
            }
          }
          if (!checked) {
            (divTableList.querySelector('#input-select-filter-' + selectedColIndex + '-all') as HTMLInputElement).checked = checked;
          }
          if (selectedFilterIndex == 'all') {
            for (let [filterIndex, filterKey] of Object.keys(this.filters[colName]).entries()) {
              (divTableList.querySelector('#input-select-filter-' + selectedColIndex + '-' + filterIndex) as HTMLInputElement).checked = checked;
              console.log('Changing Key', filterKey);
            }
          }
        }
        // }
        // this.processRegisterFilters(events, meta, searchCountryIndex, searchIndex, searchVal);
        const buttonSubmitFilters = divTableList.querySelector('#button-submit-filters-' + selectedColIndex) as HTMLButtonElement
        if (buttonSubmitFilters != null) {
          buttonSubmitFilters.style.visibility = 'visible'
        }

      })
    }

    const arrButtonSubmitFilters = divTableList.querySelectorAll('.button-submit-filters') as NodeListOf<HTMLButtonElement>;
    for (let i = 0; i < arrButtonSubmitFilters.length; i++) {
      const button = Util.clearListeners(arrButtonSubmitFilters[i])
      button.addEventListener('click', (e: any) => {
        console.log('filters applied', e.currentTarget.id, JSON.stringify(this.filters))
        this.processRegisterFilters(searchVal);
      })
    }
  }

  processRegisterFilters = (searchVal: string = "") => {
    for (var j = 0; j < this.tableData.length; j++) {
      let flagFilter = true;
      this.tableData[j].hide = false;
      const tableItemObj = this.tableData[j];
      const data = Object.values(this.tableData[j]);
      const cols = Object.keys(this.tableData[j]);
      for (var k = 0; k < cols.length; k++) {
        if (this.filters[cols[k]] != null && this.filters[cols[k]][data[k] as string] == false) {
          flagFilter = false
          this.tableData[j].hide = true;
        } else {
          console.log('notFound', k, this.filters[cols[k]])
        }
      }
      if (JSON.stringify(tableItemObj).toLowerCase().includes(searchVal.toLowerCase())) {
        if (flagFilter) {
          this.tableData[j].hide = false;
        }
      } else {
        this.tableData[j].hide = true;
      }
    }

    this.populateTable(searchVal)
  }

  loadMode = async () => {
    console.log("loadMode", this.tableData);
    if (this.tableData.length > 0) {
      this.populateTable();
    }
  }

  protected override firstUpdated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {

    this.loadMode();

  }

  override connectedCallback() {
    super.connectedCallback()
  }
  override render() {
    return html`
      <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet">
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@48,400,0,0" />
      <div part="table-outer-container" class="w-100-m-0 d-flex flex-col align-stretch">  
        <div part="table-list" id="table-list" class="w-100-m-0 flex-grow pl-10 pt-20">
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
  }
}
